import React from 'react';
import {
    List,
    Datagrid,
    TextField,
    DateField,
    useRecordContext,
    useListController,
    ListContextProvider,
    useListContext,
    ShowButton,
    Button,
    useDataProvider,
} from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, Typography, Box, Divider } from '@mui/material';

const JsonField = ({ source }) => {
    const record = useRecordContext();
    if (!record || !record[source]) return null;
    
    return (
        <pre style={{ maxHeight: '200px', overflow: 'auto' }}>
            {JSON.stringify(record[source], null, 2)}
        </pre>
    );
};

const CheckResultField = ({ source }) => {
    const record = useRecordContext();
    if (!record || !record[source]) return null;
    
    const color = record[source] === 'PASS' ? 'green' : 'red';
    return (
        <span style={{ color, fontWeight: 'bold' }}>
            {record[source]}
        </span>
    );
};

const DataSection = ({ title, data }) => {
    if (!data || Object.keys(data).length === 0) return null;

    const renderValue = (value, depth = 0) => {
        if (value === null || value === undefined) return 'N/A';
        
        if (typeof value === 'object') {
            if (Array.isArray(value)) {
                return (
                    <Box sx={{ pl: depth * 2 }}>
                        {value.map((item, index) => (
                            <Box key={index} sx={{ mb: 1 }}>
                                <Typography component="span" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                                    [{index}]:
                                </Typography>
                                <Box sx={{ pl: 2, mt: 0.5 }}>
                                    {renderValue(item, depth + 1)}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                );
            } else {
                return (
                    <Box sx={{ pl: depth * 2 }}>
                        {Object.entries(value).map(([key, val]) => (
                            <Box key={key} sx={{ mb: 1 }}>
                                {renderKeyValuePair(key, val, depth + 1)}
                            </Box>
                        ))}
                    </Box>
                );
            }
        }
        
        return (
            <Typography component="span" sx={{ color: 'text.primary' }}>
                {String(value)}
            </Typography>
        );
    };

    const renderKeyValuePair = (key, value, depth = 0) => {
        if (value === null || value === undefined) {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                        {key}:
                    </Typography>
                    <Typography sx={{ color: 'text.primary', ml: 1 }}>
                        N/A
                    </Typography>
                </Box>
            );
        }

        // For simple values (strings, numbers, booleans), display inline
        if (typeof value !== 'object' || value === null) {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                        {key}:
                    </Typography>
                    <Typography sx={{ color: 'text.primary', ml: 1 }}>
                        {String(value)}
                    </Typography>
                </Box>
            );
        }

        // For objects and arrays, display with proper indentation
        return (
            <Box sx={{ mb: 1 }}>
                <Typography sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                    {key}:
                </Typography>
                <Box sx={{ pl: 2, mt: 0.5 }}>
                    {renderValue(value, depth + 1)}
                </Box>
            </Box>
        );
    };

    const renderTopLevelValue = (value) => {
        if (typeof value === 'object' && value !== null) {
            // Special handling for PWG response structure
            if (value.dane) {
                return (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>
                            Dane firmy:
                        </Typography>
                        {Object.entries(value.dane).map(([key, val]) => (
                            <Box key={key}>
                                {renderKeyValuePair(key, val)}
                            </Box>
                        ))}
                    </Box>
                );
            }
            return (
                <Box>
                    {Object.entries(value).map(([key, val]) => (
                        <Box key={key}>
                            {renderKeyValuePair(key, val)}
                        </Box>
                    ))}
                </Box>
            );
        }
        return (
            <Typography sx={{ color: 'text.primary' }}>
                {String(value || 'N/A')}
            </Typography>
        );
    };

    return (
        <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                {title}
            </Typography>
            <Box sx={{ pl: 2 }}>
                {Object.entries(data).map(([key, value]) => (
                    <Box key={key} sx={{ mb: 3 }}>
                        {typeof value === 'object' && value !== null ? (
                            <>
                                <Typography sx={{ fontWeight: 'bold', fontSize: '1.1em', mb: 1, color: 'text.secondary' }}>
                                    {key}
                                </Typography>
                                {renderTopLevelValue(value)}
                            </>
                        ) : (
                            renderKeyValuePair(key, value)
                        )}
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

const CheckDetails = ({ checkId, onClose }) => {
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const [check, setCheck] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);

    React.useEffect(() => {
        const fetchCheck = async () => {
            try {
                setLoading(true);
                const { data } = await dataProvider.getOne(`bidders/${record.id}/checks`, {
                    id: checkId,
                });
                setCheck(data);
                setError(null);
            } catch (err) {
                console.error('Error fetching check details:', err);
                setError(err);
            } finally {
                setLoading(false);
            }
        };
        fetchCheck();
    }, [dataProvider, record.id, checkId]);

    if (loading) return <Box sx={{ p: 2 }}><Typography>Loading...</Typography></Box>;
    if (error) return (
        <Box sx={{ p: 2 }}>
            <Typography color="error">Error loading check details</Typography>
        </Box>
    );
    if (!check) return null;

    return (
        <Card variant="outlined" sx={{ mt: 2, mb: 2 }}>
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h5">
                        Check Details - {check.check_type}
                    </Typography>
                    <Button label="Back to list" onClick={onClose} />
                </Box>
                
                <Typography variant="h6" sx={{ color: check.result === 'PASS' ? 'success.main' : 'error.main', mb: 2 }}>
                    Result: {check.result}
                </Typography>

                <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" color="textSecondary">
                        Created: {new Date(check.created_at).toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Updated: {new Date(check.updated_at).toLocaleString()}
                    </Typography>
                </Box>

                <Divider />
                
                <DataSection title="Request Headers" data={check.request_headers} />
                <Divider sx={{ my: 3 }} />
                <DataSection title="Request Data" data={check.request_data} />
                <Divider sx={{ my: 3 }} />
                <DataSection title="Response Headers" data={check.response_headers} />
                <Divider sx={{ my: 3 }} />
                <DataSection title="Response Data" data={check.response_data} />
            </CardContent>
        </Card>
    );
};

const ViewDetailsButton = ({ onViewDetails }) => {
    const record = useRecordContext();
    if (!record) return null;

    return (
        <Button
            label="View Details"
            onClick={(e) => {
                e.stopPropagation();
                onViewDetails(record.id);
            }}
        />
    );
};

const BidderChecksDatagrid = ({ onViewDetails }) => {
    const { data, isLoading } = useListContext();

    if (isLoading || !data) return null;

    return (
        <Datagrid bulkActionButtons={false}>
            <TextField source="check_type" />
            <CheckResultField source="result" />
            <DateField source="created_at" showTime />
            <DateField source="updated_at" showTime />
            <ViewDetailsButton onViewDetails={onViewDetails} />
        </Datagrid>
    );
};

export const BidderChecksList = () => {
    const record = useRecordContext();
    const [selectedCheckId, setSelectedCheckId] = React.useState(null);
    const resource = record ? `bidders/${record.id}/checks` : null;
    const listContext = useListController({
        resource,
        sort: { field: 'created_at', order: 'DESC' },
        perPage: 25,
        disableSyncWithLocation: true,
    });

    if (!record) return null;
    if (listContext.isLoading) return null;
    if (listContext.error) return <div>Error loading checks</div>;

    return (
        <ListContextProvider value={listContext}>
            <div style={{ marginTop: '1em' }}>
                {selectedCheckId ? (
                    <CheckDetails 
                        checkId={selectedCheckId} 
                        onClose={() => setSelectedCheckId(null)} 
                    />
                ) : (
                    <BidderChecksDatagrid 
                        onViewDetails={setSelectedCheckId} 
                    />
                )}
            </div>
        </ListContextProvider>
    );
}; 